import type { SharedMarketingProps } from '@/types/marketing'

export interface Country {
  name: string
  code: string
  dial_code: string
  flag: string
}

export type Currency = 'USD' | 'EUR' | 'INR' | 'THB'
export type BillingCycle = 'monthly' | 'yearly'

export interface TokenCharges {
  USD: number
  EUR: number
  INR: number
  THB: number
}

export interface PricingDatum {
  currency: Currency
  actual_price: number
  discounted_price: number
}

export interface Gateways {
  stripe: boolean
  razorpay: boolean
  fake: boolean
}

export interface ApplyPageProps extends SharedMarketingProps {
  countries: Country[]
  geo_country_code: string
  pricing: Record<string, Record<BillingCycle, Record<Currency, PricingDatum>>>
  default_currency: Currency
  supported_currencies: Currency[]
  plans: Record<string, PlanInfo[]>
  invitation_code: string
  gateways: Gateways
  razorpay_key_id: string | null
  token_charges: TokenCharges
}

export interface PersonalInfo {
  first_name: string
  last_name: string
  email: string
  phone: string
  phone_dial_code: string
  phone_iso_code: string
  website: string
}

export interface Address {
  street_address: string
  address_line_2: string
  city: string
  state: string
  postal_code: string
  country: string
}

export interface RoleInfo {
  role: 'artist' | 'label' | ''
  artist_name: string
  label_name: string
  artists_under_management: string
}

export interface SocialMedia {
  instagram: string
  tiktok: string
  twitter: string
  facebook: string
}

export interface Subscription {
  plan: string
  billing_cycle: BillingCycle
  currency: Currency
  invitation_code: string
}

export interface PlanInfo {
  key: string
  name: string
  subtitle: string
  royalties: string
  badge?: string | null
  bullets: string[]
}

export interface Agreements {
  terms_of_service: boolean
  ai_music_policy: boolean
  reapply_policy: boolean
}

export interface FormData {
  personal_info: PersonalInfo
  address: Address
  role_info: RoleInfo
  demo_links: string[]
  social_media: SocialMedia
  subscription: Subscription
  agreements: Agreements
}

// A partial, group-aware patch for FormData. Used by mergeFormData (and the
// WebMCP prefill tool) to set several fields at once without clobbering the
// rest of a group. demo_links is replaced wholesale when present.
export interface PartialFormData {
  personal_info?: Partial<PersonalInfo>
  address?: Partial<Address>
  role_info?: Partial<RoleInfo>
  demo_links?: string[]
  social_media?: Partial<SocialMedia>
  subscription?: Partial<Subscription>
  agreements?: Partial<Agreements>
}

export interface StepProps {
  formData: FormData
  updateField: <S extends keyof FormData>(step: S, field: keyof FormData[S], value: FormData[S][keyof FormData[S]]) => void
  errors: Record<string, string>
  clearError: (field: string) => void
}

export interface PaymentCompleteProps extends SharedMarketingProps {
  token: string
  value?: number | null
  currency?: string | null
  plan_slug?: string | null
}

export interface ApplicationStatusProps extends SharedMarketingProps {
  token: string
  application: {
    status: string
    payment_status: string
    payment_success: boolean
    can_retry_payment: boolean
    mandate_active: boolean
    applicant_email: string
  } | null
  gateways: Gateways
  // A paid application whose mandate was revoked can re-establish it here.
  needs_mandate_setup: boolean
  mandate_gateways: Gateways
  razorpay_key_id: string | null
}

export const TOTAL_STEPS = 6

export const STEP_LABELS = [
  'Personal Info',
  'Address',
  'Role',
  'Demo Links',
  'Social Media',
  'Plan & Payment'
] as const
